import { Chip, Stack, Tooltip } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { fDateTime } from "utils/formatTime";
import type { ListingPageViewsDocument } from "../../../@types/sharedSchema";

export function getTotalViews(viewData: ListingPageViewsDocument | null): number {
  if (!viewData) return 0;
  return (viewData.totalPageViews ?? 0) + (viewData.totalListingDetailViews ?? 0);
}

export function getDistinctListingsViewed(viewData: ListingPageViewsDocument | null): number {
  if (!viewData) return 0;
  return Object.keys(viewData.listingDetailViewCounts ?? {}).length;
}

type PageViewStatsProps = {
  viewData: ListingPageViewsDocument | null;
};

export function PageViewStats({ viewData }: PageViewStatsProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!viewData) return null;

  const totalViews = getTotalViews(viewData);
  const distinctListings = getDistinctListingsViewed(viewData);

  if (totalViews === 0) return null;

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Tooltip title={t("linksControlPanel.totalViewsTip")} arrow>
        <Chip
          size="small"
          icon={<Visibility sx={{ fontSize: 14 }} />}
          label={`${totalViews} ${t("linksControlPanel.totalViewsLabel")}`}
          color="success"
          variant="outlined"
        />
      </Tooltip>
      {distinctListings > 0 && (
        <Tooltip title={t("linksControlPanel.distinctListingsTip")} arrow>
          <Chip
            size="small"
            icon={<Visibility sx={{ fontSize: 14 }} />}
            label={`${distinctListings} ${t("linksControlPanel.distinctListingsLabel")}`}
            color="info"
            variant="outlined"
          />
        </Tooltip>
      )}
    </Stack>
  );
}

type ListingDetailViewCountProps = {
  listingId: string;
  viewData: ListingPageViewsDocument | null;
};

export function ListingDetailViewCount({
  listingId,
  viewData
}: ListingDetailViewCountProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!viewData) return null;

  const count = viewData.listingDetailViewCounts?.[listingId] ?? 0;

  if (count === 0) return null;

  return (
    <Tooltip title={t("linksControlPanel.listingViewedByContact")} arrow>
      <Chip
        size="small"
        icon={<Visibility fontSize="small" />}
        label={count}
        color="info"
        variant="outlined"
        sx={{ height: 22 }}
      />
    </Tooltip>
  );
}
